<template>
  <div
    class="phone-attach"
    data-testid="spe-phone-attach"
    @keydown.enter="onEnter"
  >
    <div v-if="step === 'idle'" class="phone-attach__idle">
      <p class="phone-attach__intro">
        {{ $t('profile.student.editForm.phoneAttach.intro') }}
      </p>
      <button
        type="button"
        class="phone-attach__button phone-attach__button--secondary"
        data-testid="spe-phone-add"
        @click="start"
      >
        {{ $t('profile.student.editForm.phoneAttach.add') }}
      </button>
    </div>

    <div v-else-if="step === 'phone'" class="phone-attach__step">
      <label for="spe-phone-new" class="uc-form-label">{{
        $t('profile.student.editForm.phone')
      }}</label>
      <input
        id="spe-phone-new"
        ref="phoneInput"
        v-model.trim="phoneText"
        class="uc-form-text-input ltr"
        type="tel"
        inputmode="tel"
        autocomplete="tel"
        dir="ltr"
        data-testid="spe-phone-new"
        :placeholder="$t('login.mobile.phonePlaceholder')"
        :aria-invalid="error ? 'true' : null"
        :aria-describedby="error ? 'spe-phone-attach-error' : null"
        @beforeinput="onBeforeInput"
        @input="onPhoneInput"
      />
      <div class="phone-attach__actions">
        <button
          type="button"
          class="phone-attach__button"
          data-testid="spe-phone-send"
          :disabled="!phoneInfo.isValid || busy"
          @click="sendCode"
        >
          <span v-if="busy" class="uc-form-spinner" aria-hidden="true"></span>
          {{ $t('profile.student.editForm.phoneAttach.sendCode') }}
        </button>
        <button
          type="button"
          class="phone-attach__link"
          data-testid="spe-phone-cancel"
          :disabled="busy"
          @click="cancel"
        >
          {{ $t('profile.student.editForm.phoneAttach.cancel') }}
        </button>
      </div>
    </div>

    <div v-else class="phone-attach__step">
      <p class="phone-attach__intro" data-testid="spe-phone-code-intro">
        {{ $t('profile.student.editForm.phoneAttach.codeIntro') }}
        <bdi dir="ltr" class="phone-attach__number">{{
          phoneInfo.formatNational
        }}</bdi>
      </p>
      <div
        role="group"
        class="phone-attach__otp"
        dir="ltr"
        :aria-label="$t('profile.student.editForm.phoneAttach.codeLabel')"
        :aria-describedby="error ? 'spe-phone-attach-error' : null"
      >
        <simple-otp-input
          ref="otpInput"
          v-model="otpCode"
          data-testid="spe-phone-otp"
          :length="6"
          :paste-delay-ms="192"
          :input-props="otpInputProps"
          with-web-otp
        />
      </div>
      <div class="phone-attach__actions">
        <button
          type="button"
          class="phone-attach__button"
          data-testid="spe-phone-confirm"
          :disabled="otpCode.length !== 6 || busy"
          @click="confirm"
        >
          <span v-if="busy" class="uc-form-spinner" aria-hidden="true"></span>
          {{ $t('profile.student.editForm.phoneAttach.confirm') }}
        </button>
        <button
          type="button"
          class="phone-attach__link"
          data-testid="spe-phone-edit-number"
          :disabled="busy"
          @click="editNumber"
        >
          {{ $t('profile.student.editForm.phoneAttach.editNumber') }}
        </button>
      </div>
    </div>

    <p
      v-if="error"
      id="spe-phone-attach-error"
      class="alert alert-danger"
      role="alert"
      data-testid="spe-phone-error"
    >
      {{ error }}
    </p>
  </div>
</template>

<script>
// P43-04: add a verified mobile number to an existing student account
// (created with an email or a Google login) from Edit Profile.
//
// The proof of ownership is the same OTP challenge the mobile login uses
// (AuthService.sendMobileChallenge + confirmMobileVerification). It yields an
// opaque grant; the grant is then handed to StudentService.attachPhone, which
// is the only call that changes the account. This component never calls the
// mobile LOGIN or REGISTER routes, so no session is created or replaced and no
// account is created. The number itself is sent only to the SMS challenge --
// the server reads it from the grant, never from the attach request.
import SimpleOtpInput from 'vue-simple-otp-input'
import AuthService from '@/services/AuthService'
import StudentService from '@/services/StudentService'
import { isAllowedPhoneText, parseIranMobile } from '@/utils/mobile-phone-input'

const OTP_LENGTH = 6
const EMPTY_PHONE_INFO = { isValid: false, e164: '', formatNational: '' }

export default {
  name: 'StudentPhoneAttach',
  components: { SimpleOtpInput },
  data() {
    return {
      step: 'idle', // idle -> phone -> code
      phoneText: '',
      lastAllowedPhoneText: '',
      phoneInfo: { ...EMPTY_PHONE_INFO },
      challengeId: null,
      ownershipGrantId: null,
      otpCode: '',
      busy: false,
      error: null,
    }
  },
  computed: {
    otpInputProps() {
      return {
        inputmode: 'numeric',
        autocomplete: 'one-time-code',
        'aria-invalid': this.error ? 'true' : null,
      }
    },
  },
  methods: {
    start() {
      this.step = 'phone'
      this.error = null
      this.$nextTick(() => {
        const input = this.$refs.phoneInput
        if (input && typeof input.focus === 'function') input.focus()
      })
    },
    cancel() {
      this.step = 'idle'
      this.phoneText = ''
      this.lastAllowedPhoneText = ''
      this.phoneInfo = { ...EMPTY_PHONE_INFO }
      this.challengeId = null
      this.ownershipGrantId = null
      this.otpCode = ''
      this.error = null
    },
    editNumber() {
      this.challengeId = null
      this.ownershipGrantId = null
      this.otpCode = ''
      this.error = null
      this.start()
    },
    // A refused character never lands (a typed key, a drop, a paste); a
    // composition is checked once it is committed.
    onBeforeInput(event) {
      if (event.isComposing || event.inputType === 'insertCompositionText') {
        return
      }
      let text = typeof event.data === 'string' ? event.data : ''
      if (!text && event.dataTransfer) {
        text = event.dataTransfer.getData('text/plain') || ''
      }
      if (!isAllowedPhoneText(text)) event.preventDefault()
    },
    onPhoneInput(event) {
      const composing = Boolean(event && event.isComposing)
      if (!composing && !isAllowedPhoneText(this.phoneText)) {
        this.phoneText = this.lastAllowedPhoneText
        this.phoneInfo = parseIranMobile(this.phoneText)
        return
      }
      if (!composing) this.lastAllowedPhoneText = this.phoneText
      this.error = null
      this.phoneInfo = parseIranMobile(this.phoneText)
    },
    // Enter inside this widget's text fields (the number, the code boxes) belongs
    // to the widget: it sends the code or confirms, and it must never submit the
    // profile form around it. On a BUTTON (Cancel, Edit number, ...) Enter is
    // left alone, so a keyboard user can still press it.
    onEnter(event) {
      const target = event && event.target
      if (!target || target.tagName !== 'INPUT') return
      // Enter that commits an IME composition is not a request to send
      if (event.isComposing) return
      event.preventDefault()
      if (this.busy) return
      if (this.step === 'phone') this.sendCode()
      else if (this.step === 'code') this.confirm()
    },
    async sendCode() {
      if (this.busy || !this.phoneInfo.isValid) return
      this.busy = true
      this.error = null
      try {
        const { challengeId } = await AuthService.sendMobileChallenge(
          this.phoneInfo.e164
        )
        this.challengeId = challengeId
        this.otpCode = ''
        this.step = 'code'
        this.$nextTick(() => {
          const root = this.$refs.otpInput && this.$refs.otpInput.$el
          const input = root && root.querySelector('input')
          if (input && typeof input.focus === 'function') input.focus()
        })
      } catch (err) {
        this.error = this.mapError(err, 'send')
      } finally {
        this.busy = false
      }
    },
    async confirm() {
      if (this.busy) return
      this.busy = true
      this.error = null
      // The grant outlives a failed attach: a network error or a 5xx on the
      // attach call leaves it on the server (it is spent only by an answer), so
      // a retry sends the SAME grant and needs neither the code nor a new SMS.
      if (!this.ownershipGrantId) {
        if (this.otpCode.length !== OTP_LENGTH || !this.challengeId) {
          this.busy = false
          return
        }
        try {
          const res = await AuthService.confirmMobileVerification({
            challengeId: this.challengeId,
            code: this.otpCode,
          })
          this.ownershipGrantId = res.ownershipGrantId
        } catch (err) {
          this.error = this.mapError(err, 'confirm')
          this.otpCode = ''
          this.busy = false
          return
        }
      }
      try {
        const profile = await StudentService.attachPhone(this.ownershipGrantId)
        this.$emit('attached', profile)
        this.cancel()
      } catch (err) {
        const message = this.mapError(err, 'attach')
        const code = err && err.code
        if (code === 'PHONE_GRANT_INVALID' || code === 'PHONE_IN_USE') {
          // A spent or expired grant cannot be retried, and a number that
          // belongs to someone else needs another one: start again from the
          // number, keeping the explanation on screen.
          this.editNumber()
        } else if (code === 'PHONE_ALREADY_SET') {
          // The account has a number already and the grant is spent: nothing
          // is left to retry.
          this.cancel()
        }
        // Any other failure keeps the code step and the grant: Confirm retries.
        this.error = message
      } finally {
        this.busy = false
      }
    },
    mapError(err, stage) {
      const status = err && err.status
      const code = err && err.code
      const t = key =>
        this.$t(`profile.student.editForm.phoneAttach.error.${key}`)
      if (status === 404) return t('unavailable')
      if (status === 429) return t('tooManyRequests')
      if (stage === 'send' && status === 422) return t('invalidPhone')
      if (stage === 'confirm' && status === 401) return t('invalidOtp')
      if (stage === 'attach') {
        if (code === 'PHONE_IN_USE') return t('inUse')
        if (code === 'PHONE_ALREADY_SET') return t('alreadySet')
        if (code === 'PHONE_GRANT_INVALID' || status === 422) {
          return t('expired')
        }
      }
      return t('generic')
    },
  },
}
</script>

<style lang="scss" scoped>
.phone-attach {
  margin-top: 8px;

  &__intro {
    margin: 0 0 10px;
    font-size: 13px;
    line-height: 1.7;
    color: #52525b;
  }

  &__number {
    font-weight: 700;
    color: #1e1b4b;
  }

  &__otp {
    margin: 8px 0 12px;
  }

  &__actions {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    margin-top: 10px;
  }

  &__button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: 44px;
    padding: 0 18px;
    border: 0;
    border-radius: 12px;
    background: #4f46e5;
    color: #fff;
    font: inherit;
    font-weight: 700;
    cursor: pointer;

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    &--secondary {
      background: #eef2ff;
      color: #3730a3;
    }
  }

  &__link {
    min-height: 44px;
    padding: 0 6px;
    border: 0;
    background: none;
    color: #4f46e5;
    font: inherit;
    font-weight: 600;
    cursor: pointer;

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  .alert {
    margin-top: 12px;
  }
}
</style>
